import React, { useState, useEffect } from "react";
import tw from "tailwind-styled-components";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import DropZone from "../Notice/Dropzone";
import { toast } from "react-toastify";
import { ActionSection, CombineInputGroup, FieldWrappers, InputGroup, Label, RupeeIcon, ScrollableContent, SideCancelBtn, SubmitBtn, Wrapper } from "../Styles/InputStyles";
import { MdClose } from "react-icons/md";
import { FaRegCalendar } from "react-icons/fa";
import SelectItemModal from "../SelectItemModal";
import CategoryCard from "../CategoryCard";
import SelectMultipleRoomModal from "./SelectMutipleRoomModal";
import SelectSingleRoomModal from "./SelectSingleRoomModal";
import { capitalizeFirstLetter } from "../../helpers";


const AddElectricityBill = ({ data, propId, addBillMutate, handleClose, addBillLoading, type, propertyDetails }) => {
    const [height, setHeight] = useState(window.innerHeight);
    const [calendarOpen, setCalendarOpen] = useState(false);
    const [endCalendarOpen, setEndCalendarOpen] = useState(false);
    const [floorModalOpen, setFloorModalOpen] = useState(false);
    const [roomModalOpen, setRoomModalOpen] = useState(false);
    const [roomList, setRoomList] = useState([]);
    const [selectedFloor, setSelectedFloor] = useState(null);
    const [selectedRoom, setSelectedRoom] = useState(null);
    const [isPreviousReadingAutoFilled, setIsPreviousReadingAutoFilled] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            setHeight(window.innerHeight);
        };

        window.addEventListener('resize', handleResize);
        handleResize();

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const getPreviousReadingForType1 = (roomId) => {
        if (!roomId || !data || !data.data || !data.data.length) {
            return "";
        }

        for (const floor of data.data) {
            if (floor.rooms && floor.rooms.length > 0) {
                const room = floor.rooms.find(r => r.id.toString() === roomId.toString());
                if (room && room.prevBill && room.prevBill.length > 0) {
                    return room.prevBill[0].previousReading || "";
                }
            }
        }
        return "";
    };

    const getPreviousReadingForType2 = (floorId) => {
        if (!floorId || !data || !data.data || !data.data.length) {
            return "";
        }

        const selectedFloor = data.data.find(f => f.id.toString() === floorId.toString());
        if (selectedFloor && selectedFloor.prevBill && selectedFloor.prevBill.length > 0) {
            return selectedFloor.prevBill[0].previousReading || "";
        }
        return "";
    };

    const getPreviousReading = (roomId, floorId = null) => {
        if (type === 1) {
            return getPreviousReadingForType1(roomId);
        } else if (type === 2) {
            return getPreviousReadingForType2(floorId);
        }
        return "";
    };

    const getRoomsWithAC = () => {
        if (type === 2 && selectedRoom && selectedRoom.length > 0) {
            return selectedRoom.filter(room =>
                room.amenities && room.amenities.includes('AC')
            );
        }
        return [];
    };

    const validationSchema = Yup.object().shape({
        floor: Yup.string().required("Please select Floor"),
        roomId: Yup.string().required("Please select Room"),
        startDate: Yup.date().required("Start Date required").typeError("Start Date required"),
        endDate: Yup.date()
            .required("End Date required")
            .typeError("End Date required")
            .min(Yup.ref('startDate'), "End date must be after start date"),
        electricityChargesType: Yup.number().required(),
        amount: Yup.number()
            .when('electricityChargesType', {
                is: 1,
                then: (schema) => schema
                    .required("Amount required")
                    .min(1, "Amount must be greater than 0")
                    .max(50000, "Amount cannot exceed 50000")
                    .typeError("Amount must be a number"),
                otherwise: (schema) => schema.notRequired()
            }),
        previousReading: Yup.number()
            .when('electricityChargesType', {
                is: (val) => val !== 1,
                then: (schema) => schema
                    .required("Previous reading required")
                    .min(0, "Previous reading cannot be negative")
                    .typeError("Previous reading must be a number"),
                otherwise: (schema) => schema.notRequired()
            }),
        currentReading: Yup.number()
            .when('electricityChargesType', {
                is: (val) => val !== 1,
                then: (schema) => schema
                    .required("Current reading required")
                    .min(0, "Current reading cannot be negative")
                    .typeError("Current reading must be a number")
                    .test('is-greater', 'Current reading must be greater than previous', function (value) {
                        const { previousReading } = this.parent;
                        if (previousReading !== undefined && value !== undefined) {
                            return value > previousReading;
                        }
                        return true;
                    }),
                otherwise: (schema) => schema.notRequired()
            }),
        file: Yup.mixed().nullable()
    });

    const getInitialValues = () => {
        const baseValues = {
            floor: "",
            roomId: "",
            startDate: "",
            endDate: "",
            electricityChargesType: data?.electricityChargesType,
            amount: "",
            previousReading: "",
            currentReading: "",
            unitPrice: data?.pricePerUnit,
            file: null,
            description: "",
        };

        return baseValues;
    };

    const handleFloorSelect = (floor, setFieldValue) => {
        if (floor) {
            setFieldValue("floor", floor.id);
            setSelectedFloor(floor);
            setFieldValue("roomId", "");
            setSelectedRoom(null);

            const selectedFloorData = data.data.find(f => f.id.toString() === floor.id.toString());
            setRoomList(selectedFloorData?.rooms || []);
            if (type === 2) {
                const previousReading = getPreviousReadingForType2(floor.id);
                if (previousReading) {
                    setFieldValue("previousReading", previousReading);
                    setIsPreviousReadingAutoFilled(true);
                } else {
                    setFieldValue("previousReading", "");
                    setIsPreviousReadingAutoFilled(false);
                }
            }
        } else {
            setFieldValue("floor", "");
            setSelectedFloor(null);
            setFieldValue("roomId", "");
            setSelectedRoom(null);
            setFieldValue("previousReading", "");
            setIsPreviousReadingAutoFilled(false);
            setRoomList([]);

        }
        setFloorModalOpen(false);
    };

    const handleSingleRoomSelect = (room, setFieldValue) => {
        if (room) {
            setFieldValue("roomId", room.id);
            setSelectedRoom([room]);

            const previousReading = getPreviousReading(room.id, selectedFloor?.id);
            if (previousReading) {
                setFieldValue("previousReading", previousReading);
                setIsPreviousReadingAutoFilled(true);
            } else {
                setFieldValue("previousReading", "");
                setIsPreviousReadingAutoFilled(false);
            }
        } else {
            setFieldValue("roomId", "");
            setSelectedRoom(null);
            setFieldValue("previousReading", "");
            setIsPreviousReadingAutoFilled(false);
        }
        setRoomModalOpen(false);
    };

    const handleRoomSelect = (rooms, setFieldValue) => {
        if (rooms && rooms.length > 0) {
            const roomIds = rooms.map(room => room.id);
            setFieldValue("roomId", roomIds.join(","));
            setSelectedRoom(rooms);
            if (rooms.length > 0) {
                const previousReading = getPreviousReading(rooms[0].id, selectedFloor?.id);
                if (previousReading) {
                    setFieldValue("previousReading", previousReading);
                    setIsPreviousReadingAutoFilled(true);
                } else {
                    setFieldValue("previousReading", "");
                    setIsPreviousReadingAutoFilled(false);
                }
            } else {
                setFieldValue("previousReading", "");
                setIsPreviousReadingAutoFilled(false);
            }
        } else {
            setFieldValue("roomId", "");
            setSelectedRoom([]);
            setFieldValue("previousReading", "");
            setIsPreviousReadingAutoFilled(false);
        }
        setRoomModalOpen(false);
    };

    const handleSubmit = (values) => {
        if (type === 2 && values.electricityChargesType === 2) {
            const totalReading = values.currentReading - values.previousReading;

            const roomsWithAC = getRoomsWithAC();
            let totalAcReading = 0;

            roomsWithAC.forEach(room => {
                const acReadingField = `acReading_${room.id}`;
                const readingValue = values[acReadingField];
                if (readingValue && readingValue !== "") {
                    totalAcReading += Number(readingValue);
                }
            });

            if (totalAcReading > totalReading) {
                toast.error(`Sum of AC readings (${totalAcReading}) cannot exceed total consumption (${totalReading})`);
                return;
            }
        }
        const formData = new FormData();
        formData.append("propId", propId);
        formData.append("startDate", values.startDate);
        formData.append("endDate", values.endDate);
        formData.append("electricityChargesType", values.electricityChargesType);
        formData.append("roomFlatId", values.roomId);
        formData.append("description", values.description);
        if (values.electricityChargesType === 1) {
            formData.append("amount", values.amount);
        } else {
            formData.append("previousReading", values.previousReading);
            formData.append("currentReading", values.currentReading);

            if (type === 2) {

                const acReadings = [];

                const roomsWithAC = getRoomsWithAC();
                roomsWithAC.forEach(room => {
                    const acReadingField = `acReading_${room.id}`;
                    const readingValue = values[acReadingField];
                    if (readingValue && readingValue !== "") {
                        acReadings.push({
                            roomId: room.id,
                            reading: Number(readingValue)
                        });
                    }
                });

                if (acReadings.length > 0) {
                    formData.append("acReading", JSON.stringify(acReadings));
                }
            }
        }

        if (values.file) {
            formData.append("file", values.file[0]);
        }

        addBillMutate(formData);
    };

    const renderSelectedRoom = (selected = []) => {
        if (!selected || !selected.length) return "Select Room";

        const maxToShow = 2;
        const namesToShow = selected.slice(0, maxToShow).map(room => room.roomNum);
        const remainingCount = selected.length - maxToShow;

        return (
            <div className="flex  text-left">
                <span>{namesToShow.join(", ")}</span>
                {remainingCount > 0 && (
                    <span className=" ml-1">+{remainingCount} more</span>
                )}
            </div>
        );
    };


    return (
        <Container style={{ height: `${height}px`, overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <Header>
                <h1 className="text-white text-lg font-medium">Add Electricity Bill</h1>
                <button onClick={handleClose}>
                    <MdClose color="white" size={24} />
                </button>
            </Header>
            <ProfileSection className="flex justify-between">
                <UserInfo>
                    <span className="font-medium mb-0">
                        {capitalizeFirstLetter(propertyDetails?.name)}
                    </span>
                    <span className="text-sm text-gray-600">
                        {propertyDetails?.address}
                    </span>
                </UserInfo>
                {data.electricityChargesType === 2 && (
                    <div className="flex flex-col gap-0">
                        <span className='text-gray-500 text-sm'>{'Unit Price'}</span>
                        <span className='text-xl font-semibold text-gray-700'>{'₹' + data?.pricePerUnit}</span>
                    </div>)}
            </ProfileSection>


            <div className="px-6 flex flex-col justify-between" >
                <Formik
                    initialValues={getInitialValues()}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ values, setFieldValue, resetForm, errors, touched }) => {
                        const roomsWithAC = getRoomsWithAC();

                        return (
                            <Form>
                                <ScrollableContent >
                                    <Wrapper>
                                        <InputGroup>
                                            <Label>{type === 1 ? "Select Floor *" : "Select Flat *"}</Label>
                                            <CategoryCard
                                                title={type === 1 ? "Floor" : "Flat"}
                                                selectedCategory={selectedFloor}
                                                onClick={() => setFloorModalOpen(true)}
                                            />
                                            {errors.floor && touched.floor && (
                                                <div className="text-red-500 text-sm mt-1">{errors.floor}</div>
                                            )}
                                        </InputGroup>

                                        <InputGroup>
                                            <Label>Select Room *</Label>
                                            {roomList.length > 0 ? (
                                                <>
                                                    <CategoryCard
                                                        title="Room"
                                                        selectedCategory={
                                                            selectedRoom && selectedRoom.length > 0
                                                                ? {
                                                                    label: renderSelectedRoom(selectedRoom),
                                                                    items: selectedRoom
                                                                }
                                                                : null
                                                        }
                                                        onClick={() => setRoomModalOpen(true)}
                                                    />
                                                    {errors.roomId && touched.roomId && (
                                                        <div className="text-red-500 text-sm mt-1">{errors.roomId}</div>
                                                    )}
                                                </>
                                            ) : (
                                                <div className="w-full h-11 px-2 py-3 items-center border border-gray-200 rounded-lg bg-gray-50 text-center">
                                                    <p className="text-gray-500 text-sm">
                                                        {selectedFloor
                                                            ? `There are no rooms in the selected ${type === 2 ? "flat" : "floor"}`
                                                            : `Please select a ${type === 2 ? "flat" : "floor"} first`
                                                        }
                                                    </p>
                                                </div>
                                            )}
                                        </InputGroup>
                                        <CombineInputGroup >
                                            <InputGroup>
                                                <Label htmlFor="startDate">Start Date *</Label>
                                                <FieldWrappers className={
                                                    touched.startDate && errors.startDate
                                                        ? "border-rose-400 relative w-full"
                                                        : "border-gray-300 relative w-full"
                                                }>
                                                    <DatePicker
                                                        selected={values.startDate ? new Date(values.startDate) : null}
                                                        onChange={(date) =>
                                                            setFieldValue("startDate", moment(date).format("YYYY-MM-DD"))
                                                        }
                                                        dateFormat="yyyy-MM-dd"
                                                        className="cursor-pointer"
                                                        placeholderText="YYYY-MM-DD"
                                                        onClickOutside={() => setCalendarOpen(false)}
                                                        onSelect={() => setCalendarOpen(false)}
                                                        open={calendarOpen}
                                                        onInputClick={() => setCalendarOpen(true)}
                                                        popperPlacement="top-end"
                                                        showIcon
                                                        icon={<FaRegCalendar className="text-gray-400 text-xs mt-0.5" />}
                                                        showPopperArrow={false}
                                                        onFocus={(e) => e.target.blur()}
                                                        wrapperClassName="w-full"
                                                        portalId="root-portal"
                                                        popperClassName="react-datepicker-popper"
                                                    />
                                                </FieldWrappers>

                                            </InputGroup>

                                            <InputGroup>
                                                <Label htmlFor="endDate">End Date *</Label>
                                                <FieldWrappers className={
                                                    touched.endDate && errors.endDate
                                                        ? "border-rose-400 relative w-full"
                                                        : "border-gray-300 relative w-full"
                                                }>
                                                    <DatePicker
                                                        selected={values.endDate ? new Date(values.endDate) : null}
                                                        onChange={(date) =>
                                                            setFieldValue("endDate", moment(date).format("YYYY-MM-DD"))
                                                        }
                                                        minDate={values.startDate ? new Date(values.startDate) : null}
                                                        dateFormat="yyyy-MM-dd"
                                                        className="cursor-pointer"
                                                        placeholderText="YYYY-MM-DD"
                                                        onClickOutside={() => setEndCalendarOpen(false)}
                                                        onSelect={() => setEndCalendarOpen(false)}
                                                        open={endCalendarOpen}
                                                        onInputClick={() => setEndCalendarOpen(true)}
                                                        popperPlacement="top-end"
                                                        showIcon
                                                        icon={<FaRegCalendar className="text-gray-400 text-xs mt-0.5" />}
                                                        showPopperArrow={false}
                                                        onFocus={(e) => e.target.blur()}
                                                        wrapperClassName="w-full"
                                                        portalId="root-portal"
                                                        popperClassName="react-datepicker-popper"
                                                    />
                                                </FieldWrappers>

                                            </InputGroup>
                                        </CombineInputGroup>
                                        {values.electricityChargesType === 1 ? (
                                            <InputGroup>
                                                <Label>Amount *</Label>
                                                <FieldWrappers>
                                                    <RupeeIcon />
                                                    <Field
                                                        name="amount"
                                                        type="number"
                                                        placeholder="Enter Amount"
                                                        className="p-2 rounded w-full pl-5 pr-4"
                                                    />
                                                </FieldWrappers>
                                                {errors.amount && touched.amount && (
                                                    <div className="text-red-500 text-sm mt-1">{errors.amount}</div>
                                                )}
                                            </InputGroup>
                                        ) : (
                                            <>
                                                <InputGroup>
                                                    <Label>Previous Reading *</Label>
                                                    <FieldWrappers className={`${isPreviousReadingAutoFilled ? 'bg-gray-100' : ''}`}>
                                                        <Field
                                                            name="previousReading"
                                                            type="number"
                                                            className="p-2 rounded w-full"
                                                            placeholder="Previous reading"
                                                            readOnly={isPreviousReadingAutoFilled}
                                                            style={isPreviousReadingAutoFilled ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                                                        />
                                                    </FieldWrappers>
                                                    {errors.previousReading && touched.previousReading && (
                                                        <div className="text-red-500 text-sm mt-1">{errors.previousReading}</div>
                                                    )}
                                                </InputGroup>

                                                <InputGroup>
                                                    <Label>Current Reading *</Label>
                                                    <FieldWrappers>
                                                        <Field
                                                            name="currentReading"
                                                            type="number"
                                                            className="p-2 rounded w-full"
                                                            placeholder="Current reading"
                                                        />
                                                    </FieldWrappers>
                                                    {errors.currentReading && touched.currentReading && (
                                                        <div className="text-red-500 text-sm mt-1">{errors.currentReading}</div>
                                                    )}
                                                </InputGroup>

                                                {type === 2 && values.electricityChargesType === 2 && roomsWithAC.length > 0 && (
                                                    <>
                                                        {roomsWithAC.map(room => (
                                                            <InputGroup key={room.id}>
                                                                <Label htmlFor={`acReading_${room.id}`}>
                                                                    AC Reading (Room: {room.roomNum})
                                                                </Label>
                                                                <FieldWrappers>
                                                                    <Field
                                                                        name={`acReading_${room.id}`}
                                                                        type="number"
                                                                        className="p-2 rounded w-full"
                                                                        placeholder={`Enter AC reading for ${room.roomNum}`}
                                                                    />
                                                                </FieldWrappers>
                                                                {errors[`acReading_${room.id}`] && touched[`acReading_${room.id}`] && (
                                                                    <div className="text-red-500 text-sm mt-1">{errors[`acReading_${room.id}`]}</div>
                                                                )}
                                                            </InputGroup>
                                                        ))}
                                                    </>
                                                )}
                                            </>
                                        )}
                                        <InputGroup>
                                            <Label htmlFor="description">Description (optional)</Label>
                                            <FieldWrapper>
                                                <Field
                                                    as={Textarea}
                                                    id="description"
                                                    name="description"
                                                    rows={4}
                                                    maxLength={500}
                                                    placeholder="Write here..."
                                                />
                                            </FieldWrapper>
                                        </InputGroup>
                                        <InputGroup>
                                            <Label htmlFor="file">Document (optional)</Label>
                                            <FieldWrapper $select={true}>
                                                <Field
                                                    required
                                                    name="file"
                                                    type="text"
                                                    id="file"
                                                    autoComplete="off"
                                                >
                                                    {(props) => (
                                                        <DropZone
                                                            fields={props.field}
                                                            setFieldValue={props.form.setFieldValue}
                                                            componentFor="expense"
                                                            acceptedFiles="image/*,application/pdf"
                                                            File={props.field.value ? [props.field.value] : null}
                                                        />
                                                    )}
                                                </Field>
                                            </FieldWrapper>
                                        </InputGroup>
                                    </Wrapper>
                                </ScrollableContent>
                                <ActionSection>
                                    <SideCancelBtn type="button" onClick={() => {
                                        resetForm();
                                        setSelectedFloor(null);
                                        setSelectedRoom(null);
                                        setRoomList([]);
                                        handleClose();
                                        setIsPreviousReadingAutoFilled(false);
                                    }}>
                                        Cancel
                                    </SideCancelBtn>
                                    <SubmitBtn type="submit" disabled={addBillLoading}>
                                        {addBillLoading ? "Submiting..." : "Submit"}
                                    </SubmitBtn>
                                </ActionSection>

                                <SelectItemModal
                                    isOpen={floorModalOpen}
                                    onClose={() => setFloorModalOpen(false)}
                                    onSelect={(floor) => handleFloorSelect(floor, setFieldValue)}
                                    options={data?.data || []}
                                    selectedItem={selectedFloor}
                                    title={type === 2 ? "Select Flat" : "Select Floor"}
                                    searchPlaceholder={type === 2 ? "Search flats..." : "Search floors..."}
                                    idKey="id"
                                    nameKey="name"
                                    descriptionKeys={["propertyName"]}
                                    emptyMessage="No floors available"
                                    noResultsMessage="No floors found"
                                    showClearSelection={true}
                                />

                                {type === 2 &&
                                    <SelectMultipleRoomModal
                                        isOpen={roomModalOpen}
                                        onClose={() => setRoomModalOpen(false)}
                                        onSelect={(room) => handleRoomSelect(room, setFieldValue)}
                                        options={roomList}
                                        selectedItem={selectedRoom}
                                        title="Select Room"
                                        searchPlaceholder="Search rooms..."
                                        idKey="id"
                                        nameKey="roomNum"
                                        descriptionKeys={["amenities"]}
                                        emptyMessage="No rooms available"
                                        noResultsMessage="No rooms found"
                                        showClearSelection={true}
                                    />
                                }

                                {type === 1 &&
                                    <SelectSingleRoomModal
                                        isOpen={roomModalOpen}
                                        onClose={() => setRoomModalOpen(false)}
                                        onSelect={(room) => handleSingleRoomSelect(room, setFieldValue)}
                                        options={roomList}
                                        selectedItem={selectedRoom}
                                        title="Select Room"
                                        searchPlaceholder="Search rooms..."
                                        idKey="id"
                                        nameKey="roomNum"
                                        descriptionKeys={["amenities"]}
                                        emptyMessage="No rooms available"
                                        noResultsMessage="No rooms found"
                                        showClearSelection={true}
                                    />
                                }

                            </Form>
                        )
                    }}
                </Formik>
            </div>
        </Container>
    );
};

const Container = tw.div`bg-white w-full h-full flex flex-col relative `;
const Header = tw.div`flex items-center justify-between bg-primary-color py-4 px-4`;
const FieldWrapper = tw.div`w-full`;
const UserInfo = tw.div`flex flex-col`;
const ProfileSection = tw.div`flex items-center gap-3 bg-gray-100 px-4 p-4`;
const Textarea = tw.textarea`min-h-30 border border-gray-300 rounded pl-2 py-2 focus:ring-1 focus:ring-sky-600  focus:border-sky-600 focus:outline-none duration-200 w-full h-30 resize-y  break-words overflow-hidden`;

export default AddElectricityBill;